// Phase 4 — components used by the Summer Volleyball dual-team flyer.
// Extracted out of Squads.jsx so the monolith stays manageable. Pure
// presentation — all data comes from props.

import React, { useState, useMemo } from "react";
import { Users, Search } from "lucide-react";
import { SKILL_BY_CODE } from "../../data";
import { playerSkillCodes, categoryBadge } from "./flyerHelpers";

// Player picker for the Team B starting + bench sections of the dual-team
// Summer Volleyball flyer. Mirrors the Team A picker, but ensures a
// player can only be in one bucket across both teams.
export function DualTeamSection({
  title, subtitle, size, roster, selected, onToggle,
  otherTeamSet, sameTeamOther, team, availabilityFor, isIneligible,
  className
}) {
  const [searchB, setSearchB] = useState("");
  const visibleRoster = useMemo(() => {
    const q = searchB.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(p =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    );
  }, [roster, searchB]);

  return (
    <section className={className ? `panel ${className}` : "panel"}>
      <h2><Users size={18}/> {title}</h2>
      {subtitle && <p style={{margin:"-8px 0 12px", color:"var(--muted)", fontSize:13}}>{subtitle}</p>}
      <div className="search">
        <Search size={16} />
        <input placeholder="Search players by name…" value={searchB} onChange={e => setSearchB(e.target.value)} />
      </div>
      <div className="players">
        {visibleRoster.map(p => {
          const isSelected = selected.includes(p.id);
          const onOtherSquad = otherTeamSet.has(p.id) || sameTeamOther.includes(p.id);
          const avStatus     = availabilityFor(p.id);
          const ineligible   = isIneligible(p.id);
          const capFull      = !isSelected && selected.length >= size;
          const disabled     = onOtherSquad || ineligible || capFull;
          const tooltip      = avStatus === "Not Available"
            ? "Said NO in availability"
            : !avStatus ? "Hasn't responded to availability yet"
            : onOtherSquad ? "Already picked elsewhere" : "";
          return (
            <label
              key={p.id}
              style={disabled ? { opacity: ineligible ? 0.55 : 0.4 } : null}
              title={tooltip}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => onToggle(p.id)}
              />
              <span>{p.name}</span>
              {avStatus === "Not Available"
                ? <em className="skillChipMini skillChipMini--unavail">Said NO</em>
                : !avStatus
                  ? <em className="skillChipMini skillChipMini--awaiting">Awaiting</em>
                  : <em>{p.roles[0] || "PLAYER"}</em>}
            </label>
          );
        })}
      </div>
    </section>
  );
}

// Renders a numbered roster list inside the .playerBox, optionally with a
// "BACKUPS" sub-section. Used by the dual-team Summer Volleyball flyer.
export function RosterList({ heading, players, capacity, bench, benchLabel, team }) {
  return (
    <>
      <h3>{heading}</h3>
      <ol>
        {Array.from({ length: capacity }).map((_, i) => {
          const p = players[i];
          const codes = p ? playerSkillCodes(team, p.id) : [];
          return (
            <li key={i}>
              <span>{i + 1}.</span>
              <b>{p ? p.name : "—"}</b>
              <span className="posterSkills">
                {codes.slice(0, 3).map(c => (
                  <em
                    key={c}
                    className={`posterSkill posterSkill--${SKILL_BY_CODE[c]?.category}`}
                    title={SKILL_BY_CODE[c]?.label}
                  >{categoryBadge(c)}</em>
                ))}
              </span>
            </li>
          );
        })}
      </ol>
      {bench && bench.length > 0 && (
        <>
          <h3 style={{marginTop:8, fontSize:14}}>{benchLabel}</h3>
          <ol>
            {bench.map((p, i) => {
              const codes = playerSkillCodes(team, p.id);
              return (
                <li key={p.id}>
                  <span>{i + 1}.</span>
                  <b>{p.name}</b>
                  {codes.length > 0 && (
                    <span className="posterSkills">
                      {codes.slice(0, 3).map(c => (
                        <em
                          key={c}
                          className={`posterSkill posterSkill--${SKILL_BY_CODE[c]?.category}`}
                          title={SKILL_BY_CODE[c]?.label}
                        >{categoryBadge(c)}</em>
                      ))}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </>
  );
}

// Per-player card used in the single-team broadcast flyer.
export function FlyerCard({ index, player, codes }) {
  const num = String(index + 1).padStart(2, "0");
  const shortRoles = codes.slice(0, 2).map(c => SKILL_BY_CODE[c]?.short || c).join(" · ");
  return (
    <div className={`flyerCard${!player ? " flyerCard--empty" : ""}`}>
      <div className="flyerCard-num">{num}</div>
      <div className="flyerCard-body">
        <b>{player ? player.name : "—"}</b>
        <span>{player ? (shortRoles || "PLAYER") : "Empty slot"}</span>
      </div>
      <div className="flyerCard-icons">
        {codes.slice(0, 2).map(c => (
          <span key={c} className={`flyerSkill flyerSkill--${SKILL_BY_CODE[c]?.category}`} title={SKILL_BY_CODE[c]?.label}>
            {categoryBadge(c)}
          </span>
        ))}
      </div>
    </div>
  );
}

// Single column of the dual-team flyer roster (used twice: A and B).
export function FlyerRosterColumn({ heading, players, bench, team, capacity, benchLabel }) {
  return (
    <div className="flyerDual-col">
      <div className="flyerDual-head">{heading}</div>
      <ol className="flyerDual-list">
        {Array.from({ length: capacity }).map((_, i) => {
          const p = players[i];
          const codes = p ? playerSkillCodes(team, p.id) : [];
          return (
            <li key={i}>
              <span className="flyerDual-num">{String(i + 1).padStart(2, "0")}</span>
              <b>{p ? p.name : "—"}</b>
              {codes.length > 0 && (
                <span className="flyerDual-skill">{categoryBadge(codes[0])}</span>
              )}
            </li>
          );
        })}
      </ol>
      {bench && bench.length > 0 && (
        <div className="flyerDual-bench">
          <small>{benchLabel}</small>
          {bench.map((p, i) => (
            <span key={p.id}>{i + 1}. {p.name}</span>
          ))}
        </div>
      )}
    </div>
  );
}
